
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import { MAIN_URL } from "../../../Configurations/Urls";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonIcon from "@mui/icons-material/Person";
import CategoryIcon from "@mui/icons-material/Category";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import toast from "react-hot-toast";
import {
  fetchAttendanceSettlementStatus,
  fetchAttendanceTimeLogs,
  settleAttendance,
} from "../../../Apis/Attendance";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  applyClientToolbarFilters,
  mergeToolbarFilters,
  ToolbarDateFilter,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";
import { hasPermissionAction } from "../../../lib/organizationPermissions";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";

const DEFAULT_COLUMNS = [
  {
    field: "employee_name",
    label: "Employee Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "employee_code",
    label: "Employee Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "attendance_date",
    label: "Attendance Date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "attendance_log_type",
    label: "Log Type",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "attendance_log_time",
    label: "Log Time",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "remarks",
    label: "Remarks",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "settlement_status",
    label: "Settlement Status",
    visible: true,
    width: 150,
    filterable: true,
    sortable: false,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_attendance_timelog_id",
  sortOrder: "desc",
};

const TIMELOG_TOOLBAR_FILTERS = [
  { urlKey: "attendance_log_type", field: "attendance_log_type" },
  {
    urlKey: "attendance_date",
    field: "attendance_date",
    operator: "date_equals",
  },
];

const ATTENDANCE_LOG_TYPE_OPTIONS = [
  { value: "Clock In", label: "Clock In" },
  { value: "Clock Out", label: "Clock Out" },
  { value: "Break Start", label: "Break Start" },
  { value: "Break End", label: "Break End" },
];

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function AttendanceTimelogsList() {
  const [leaves, setLeaves] = useState([]);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const { Permission } = usePermissionDataStore();
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [settlementEnabled, setSettlementEnabled] = useState(
    Boolean(org?.attendance_settlement_enabled)
  );
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleDate, setSettleDate] = useState(new Date());
  const [settling, setSettling] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const canSettleAttendance = useMemo(
    () =>
      hasPermissionAction(
        Permission,
        "ATTENDANCE_SETTLE||ATTENDANCE_TIME_LOG_ADD||ATTENDANCE_VIEW_ALL||ATTENDANCE_RECORDS_ALL"
      ),
    [Permission]
  );

  const showSettleButton = settlementEnabled && canSettleAttendance;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);

  useEffect(() => {
    if (!org?.organization_id) return;

    const loadSettlementStatus = async () => {
      try {
        const response = await fetchAttendanceSettlementStatus(org.organization_id);
        setSettlementEnabled(Boolean(response?.attendance_settlement_enabled));
      } catch (error) {
        setSettlementEnabled(Boolean(org?.attendance_settlement_enabled));
      }
    };

    loadSettlementStatus();
  }, [org?.organization_id, org?.attendance_settlement_enabled]);

  const handleSettleAttendance = async () => {
    if (!org?.organization_id || !settleDate) return;

    try {
      setSettling(true);
      const attendanceDate = format(settleDate, "yyyy-MM-dd");
      const response = await settleAttendance(org.organization_id, attendanceDate);
      const settled = response?.data?.settled ?? 0;
      const skipped = response?.data?.skipped ?? 0;
      const errors = response?.data?.errors ?? [];
      const baseMessage =
        response?.message ||
        `Settlement completed for ${attendanceDate}.`;
      const summaryMessage =
        errors.length > 0
          ? `${baseMessage} ${settled} settled, ${skipped} skipped, ${errors.length} failed.`
          : `${baseMessage} ${settled} settled, ${skipped} skipped.`;
      if (settled > 0) {
        toast.success(
          `${summaryMessage} Clock In/Out and Break Start/End time logs were also settled.`
        );
      } else if (errors.length > 0) {
        toast.error(summaryMessage);
      } else {
        toast.error(
          `No attendance records were settled for ${attendanceDate}. Check timelogs and work shifts.`
        );
      }
      setSettleDialogOpen(false);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
        return;
      }
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to settle attendance."
      );
    } finally {
      setSettling(false);
    }
  };

  const getFormattedAttendanceLogs = async (orgId) => {
    try {

      const data = await fetchAttendanceTimeLogs(orgId); 
      const a = data?.attendance_time_logs || []

      return a.map((item) => {
        const remarks = item?.remarks === null || item?.remarks === undefined ? "" : item.remarks;
        const isSettled = String(remarks)
          .toLowerCase()
          .includes("settled via attendance settlement");

        return {
          ...item,
          id: item.organization_person_attendance_timelog_id,
          employee_name:
            item?.employee_name ||
            `${item?.employee?.org_persons?.person?.first_name || ""}  ${item?.employee?.org_persons?.person?.middle_name || ""} ${item?.employee?.org_persons?.person?.last_name || ""}`.trim(),
          employee_code:item?.employee?.org_persons?.person_code || "",
          attendance_date: new Date(item.attendance_date).toLocaleDateString(
            "en-GB",
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }
          ),
          remarks,
          settlement_status: isSettled ? "Settled" : "",
          attendance_log_time: new Date(item.attendance_log_time).toLocaleString(
            "en-GB",
            {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }
          ),
        };
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      }
      console.error("Failed to fetch attendance logs:", err);
      return [];
    }
  };

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
         `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_person_attendance_timelogs_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          let columns = DEFAULT_COLUMNS;
          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              columns = serverCfg.columns;
            }
          }

          const hasSettlementCol = columns.some(
            (c) => (c.field || c.key) === "settlement_status"
          );
          setConfigColumns(
            hasSettlementCol
              ? columns
              : [
                  ...columns,
                  {
                    field: "settlement_status",
                    label: "Settlement Status",
                    visible: true,
                    width: 150,
                    filterable: true,
                    sortable: false,
                    pinned: "none",
                    required: false,
                  },
                ]
          );
        } else {
          setConfigColumns(DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // load attendanceTimeLog data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          TIMELOG_TOOLBAR_FILTERS
        );
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_person_attendance_timelog_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/attendance-time-logs-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const logs = await getFormattedAttendanceLogs(org.organization_id);
          data = applyClientToolbarFilters(
            logs,
            searchParams,
            TIMELOG_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        const formattedLogs = data.map((item) => {
          const remarks = item?.remarks === null || item?.remarks === undefined ? "" : item.remarks;
          const isSettled = String(remarks)
            .toLowerCase()
            .includes("settled via attendance settlement");

          return {
            ...item,
            id: item.organization_person_attendance_timelog_id,
            employee_name:
              item?.employee_name ||
              `${item?.employee?.org_persons?.person?.first_name || ""}  ${item?.employee?.org_persons?.person?.middle_name || ""} ${item?.employee?.org_persons?.person?.last_name || ""}`.trim(),
            employee_code: item?.employee_code || item?.employee?.org_persons?.person_code || "",
            attendance_date: item.attendance_date
              ? new Date(item.attendance_date).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "",
            remarks,
            settlement_status: isSettled ? "Settled" : "",
            attendance_log_time: item.attendance_log_time
              ? new Date(item.attendance_log_time).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })
              : "",
          };
        });
        
        setLeaves(formattedLogs);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch attendance logs:", err);
          toast.error("Failed to fetch attendance logs");
          setLeaves([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams, refreshKey]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/attendance-time-logs/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Attendance log deleted successfully");
          // Always trigger a fresh fetch so paired/cascaded deletes disappear immediately.
          setRefreshKey((key) => key + 1);
        } else {
          const status = response.status;
          if (status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else if (status === 422) {
            const firstError =
              data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
              "Validation failed.";
            toast.error(firstError);
          } else if (status === 409) {
            toast.error(data.error || "This log is linked to other records.");
          } else {
            toast.error(data.message || data.error || "Failed to delete log");
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete attendance log"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/attendance/time-logs/edit/${item.id || item.organization_person_attendance_timelog_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/attendance/time-logs/view/${item.id || item.organization_person_attendance_timelog_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );


  return (
    <>
      <Layout4
        loading={loading}
        heading={"Attendance Time Logs"}
        btnName={"Add Time Log"}
        add_action={"ATTENDANCE_TIME_LOG_ADD"}
        extraButtons={[
          {
            label: "Settle Attendance",
            show: showSettleButton,
            onClick: () => setSettleDialogOpen(true),
            variant: "outlined",
          },
        ]}
        Data={leaves}
        delete_action={"ATTENDANCE_TIME_LOG_DELETE"}
        tableHeaders={[
          { name: "Employee Code", value_key: "employee_code" },
          { name: "Employee Name", value_key: "name", textStyle: "capitalize" },
          { name: "Attendance Date", value_key: "attendance_date" },
          {
            name: "Attendance Log Type",
            value_key: "attendance_log_type",
            textStyle: "capitalize",
          },
          { name: "Attendance Log Time", value_key: "attendance_log_time" },
          { name: "Settlement Status", value_key: "settlement_status" },
          { name: "Remarks", value_key: "remarks", textStyle: "capitalize" },
        ]}
        Icons={[
          <PersonIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <FormatAlignJustifyIcon color="primary" />,
          <CategoryIcon sx={{ color: "text.secondary" }} />,
          <DateRangeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Attendance Time Logs",
          "Attendance Time Logs",
          "Add Time Logs",
          "Attendance Time Logs",
        ]}
        Route={"/attendance/time-logs"}
        setData={setLeaves}
        DeleteFunc={handleDelete}
      />
      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_person_attendance_timelogs"
          primaryKey="organization_person_attendance_timelog_id"
          heading="Attendance Time Logs"
          data={leaves}
          sortname="attendance_log_time"
          edit_delete_action={["ATTENDANCE_TIME_LOG_EDIT", "ATTENDANCE_TIME_LOG_DELETE"]}
          showActions={true}
          Route="/attendance/time-logs"
          DeleteFunc={handleDelete}
          handleShow={handleShow}
          EditFunc={handleEdit}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="employee_attendance_timelogs"
          toolbarExtras={
            <>
              <ToolbarFilterSelect
                label="Attendance Log Type"
                urlKey="attendance_log_type"
                allLabel="All log types"
                options={ATTENDANCE_LOG_TYPE_OPTIONS}
                valueKey="value"
                labelKey="label"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
              <ToolbarDateFilter
                label="Attendance Date"
                urlKey="attendance_date"
                searchParams={searchParams}
                onChange={handleToolbarFilterChange}
              />
            </>
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
      <Dialog
        open={settleDialogOpen}
        onClose={() => !settling && setSettleDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Settle Attendance</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <DatePicker
              label="Attendance Date"
              value={settleDate}
              onChange={(value) => value && setSettleDate(value)}
              format="dd/MM/yyyy"
              slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleDialogOpen(false)} disabled={settling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSettleAttendance}
            disabled={settling || !settleDate}
          >
            {settling ? "Settling..." : "Settle"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AttendanceTimelogsList;






